import { useCallback, useRef, useState } from 'react';
import { Card, ConfirmState, ModalState, ToastState } from '../types';
import { CardsState, useCardsState } from './useCardsState';
import { ViewNavState, useViewNav } from './useViewNav';
import { WheelSpinState, useWheelSpin } from './useWheelSpin';
import { useCardModal } from './useCardModal';
import { useConfirmDialog } from './useConfirmDialog';
import { useToast } from './useToast';

export interface SpinradState extends CardsState, ViewNavState, WheelSpinState {
  modal: ModalState;
  openModal: (id: number, deck?: number[]) => void;
  closeModal: () => void;
  goPrevModal: () => void;
  goNextModal: () => void;
  confirm: ConfirmState;
  closeConfirm: () => void;
  confirmOk: () => void;
  toast: ToastState;
  showToast: (msg: string) => void;
  todoCreateOpen: boolean;
  openTodoCreate: () => void;
  closeTodoCreate: () => void;
  activeCard: Card | undefined;
  currentStep: number;
  handleSpinDiscuss: () => void;
  handleDeleteCard: (id: number) => void;
  confirmDeleteCardFlow: (
    id: number,
    after?: () => void,
    fromWB?: boolean,
  ) => void;
  confirmDeleteNoteFlow: (cardId: number, noteId: number) => void;
  handleFinishWorkboard: () => void;
  handleDeleteWorkboard: () => void;
  coreValues: string[];
}

export const useSpinradState = (
  coreValues: string[],
  usergroupId: number | undefined,
): SpinradState => {
  const remapActiveWorkboardRef = useRef<
    (oldId: number, newId: number) => void
  >(() => {});
  const cardsState = useCardsState(usergroupId, (oldId, newId) =>
    remapActiveWorkboardRef.current(oldId, newId),
  );
  const viewState = useViewNav(cardsState.cards);
  remapActiveWorkboardRef.current = viewState.remapActiveWorkboard;
  const wheelState = useWheelSpin(cardsState.cards);
  const modalState = useCardModal();
  const confirmState = useConfirmDialog();
  const toastState = useToast();
  const [todoCreateOpen, setTodoCreateOpen] = useState(false);

  const { cards } = cardsState;
  const { currentView, activeWB } = viewState;

  const activeCard = cards.find(c => c.id === activeWB);

  const currentStep = (() => {
    if (currentView === 'wb' && activeWB != null) {
      if (!activeCard) return 2;
      const notes = activeCard.notes.filter(n => (n.text || '').trim());
      if (notes.length === 0) return 2;
      if ((activeCard.values || []).length === 0) return 3;
      if (!activeCard.rating) return 4;
      if (activeCard.todoCreated) return 6;
      return 5;
    }
    return wheelState.pendingSpin ? 2 : 1;
  })();

  const openTodoCreate = useCallback((): void => setTodoCreateOpen(true), []);
  const closeTodoCreate = useCallback((): void => setTodoCreateOpen(false), []);

  const handleSpinDiscuss = useCallback((): void => {
    if (!wheelState.pendingSpin) return;
    const id = cardsState.createCard(
      wheelState.pendingSpin.q,
      wheelState.pendingSpin.from,
    );
    wheelState.resetSpin();
    viewState.openWorkboard(id);
    toastState.showToast('Workboard erstellt – sammelt eure Notizen');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wheelState.pendingSpin]);

  const handleDeleteCard = useCallback(
    (id: number): void => {
      cardsState
        .deleteCard(id)
        .then(() => {
          viewState.clearActiveWorkboardIfMatches(id);
          toastState.showToast('Karte gelöscht');
        })
        .catch(() => {
          toastState.showToast('Fehler beim Löschen der Karte');
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const confirmDeleteCardFlow = useCallback(
    (id: number, after?: () => void, fromWB?: boolean): void => {
      const card = cards.find(c => c.id === id);
      if (!card) return;
      const draft = !card.finalized;
      let msg: string;
      if (fromWB) {
        msg = draft
          ? 'Das Workboard und die zugehörige Karte werden gelöscht. Die Karte ist noch ein Entwurf.'
          : 'Das Workboard und die zugehörige Karte werden gelöscht.';
      } else {
        msg = draft
          ? 'Diese Karte und das zugehörige Workboard werden gelöscht. Die Karte ist noch ein Entwurf.'
          : 'Diese Karte und das zugehörige Workboard werden gelöscht.';
      }
      confirmState.ask('Karte löschen?', msg, 'Löschen', () => {
        handleDeleteCard(id);
        if (after) after();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards],
  );

  const confirmDeleteNoteFlow = useCallback(
    (cardId: number, noteId: number): void => {
      confirmState.ask('Notiz endgültig löschen?', '', 'Löschen', () => {
        cardsState.deleteNote(cardId, noteId);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleFinishWorkboard = useCallback((): void => {
    if (!activeCard) return;
    if ((activeCard.values || []).length === 0) {
      toastState.showToast('Bitte mindestens einen Kernwert zuordnen');
      return;
    }
    if (!activeCard.rating) {
      toastState.showToast('Bitte mit Sternen bewerten');
      return;
    }
    cardsState
      .finalizeCard(activeCard)
      .then(saved => {
        viewState.remapActiveWorkboard(activeCard.id, saved.id);
        viewState.switchView('ov');
        toastState.showToast('Karte auf der Übersicht');
      })
      .catch(() => {
        toastState.showToast('Fehler beim Speichern der Karte');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCard]);

  const handleDeleteWorkboard = useCallback((): void => {
    if (activeWB != null) {
      confirmDeleteCardFlow(activeWB, () => viewState.switchView('ov'), true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWB, confirmDeleteCardFlow]);

  return {
    ...cardsState,
    ...viewState,
    ...wheelState,
    modal: modalState.modal,
    openModal: modalState.openModal,
    closeModal: modalState.closeModal,
    goPrevModal: modalState.goPrev,
    goNextModal: modalState.goNext,
    confirm: confirmState.confirm,
    closeConfirm: confirmState.closeConfirm,
    confirmOk: confirmState.confirmOk,
    toast: toastState.toast,
    showToast: toastState.showToast,
    todoCreateOpen,
    openTodoCreate,
    closeTodoCreate,
    activeCard,
    currentStep,
    handleSpinDiscuss,
    handleDeleteCard,
    confirmDeleteCardFlow,
    confirmDeleteNoteFlow,
    handleFinishWorkboard,
    handleDeleteWorkboard,
    coreValues,
  };
};
